/**
 * Landing-link URL construction + validation (pure, unit-testable).
 *
 * This is the FRONTEND mirror of the send-time authority in
 * supabase/functions/_shared/flightSendHelpers.ts
 * (interpolateLandingVars / appendParamsToUrl / buildLandingPageLink). The
 * backend remains authoritative at send time; this module powers the
 * FlightForm live preview + validation and MUST stay behaviourally identical
 * for the resolved path. Parity is enforced by shared test vectors
 * (landingLink.test.ts ↔ flightSendHelpers.landinglink.test.ts).
 *
 * Names are operator-authored literals and are NEVER interpolated. Only param
 * VALUES and the base_url may contain {{variable}} placeholders.
 */

// Canonical landing-link variables advertised in the FlightForm hint panel.
// Kept in sync with CONTACT_FIELDS + SYSTEM_FIELDS in templates.service.ts and
// LANDING_LINK_CONTACT_VARS in the backend.
export const LANDING_LINK_CONTACT_VARS = [
  "first_name",
  "last_name",
  "full_name",
  "email",
  "phone_number",
  "title",
  "city",
  "state",
  "postal_code",
  "member_status",
  "member_start_date",
  "member_end_date",
  "membership_id",
  "zuora_account_id",
  // Household-aware effective Zuora Account ID: the contact's own GUID, else the
  // single distinct GUID shared by their household (secondary members inherit
  // the primary's billing account), else empty. Distinct from the canonical
  // {{zuora_account_id}} direct read. Resolved server-side at send time; the
  // preview keeps it literal. See flightSendHelpers.ts resolveEffectiveZuora*.
  "effective_zuora_account_id",
  "membership_type",
  "lifecycle_stage",
  "current_date",
  "current_year",
] as const;

// Documented, UNADVERTISED legacy aliases accepted for saved-flight compat
// (these resolve to the canonical value at send time; see the backend bag).
const LEGACY_ALIAS_VARS = [
  "zip_code",
  "membership_status",
  "membership_expiration",
  "job_title",
  "company_name",
  "contact_type",
] as const;

// All placeholder names the send path can resolve (so an "unknown placeholder"
// warning is accurate). Canonical + runtime + documented aliases.
export const KNOWN_LANDING_VARS: ReadonlySet<string> = new Set<string>([
  ...LANDING_LINK_CONTACT_VARS,
  ...LEGACY_ALIAS_VARS,
]);

// Fixed param keys, in the exact order the backend appends them.
export const LANDING_FIXED_PARAM_KEYS = [
  "utm_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "affid",
  "promo",
  "ste",
] as const;

// Registration-prefill param names (landing page's parsePrefillParams keys).
export const LANDING_PREFILL_KEYS = [
  "email",
  "first_name",
  "last_name",
  "phone",
] as const;

export interface CustomParam {
  name: string;
  value: string;
  // Opt-in: a recipient missing this value must not get a broken link. The
  // pre-send modal hard-blocks and the send path excludes such recipients.
  required?: boolean;
}

// Allowed characters for a custom parameter NAME (operator-authored literal;
// restricted so it can never inject URL syntax). Mirrors the backend regex.
export const LANDING_PARAM_NAME_RE = /^[A-Za-z0-9_.-]+$/;

export interface LandingConfig {
  enabled?: boolean;
  base_url?: string;
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  affid?: string;
  promo?: string;
  ste?: string;
  prefill_registration?: boolean;
  custom_params?: CustomParam[];
}

const PLACEHOLDER_RE = /\{\{\s*(\w+)\s*\}\}/g;

// Substitute {{var}} against the bag; unknown names → '' AND recorded.
// opts.encodeValues (base_url): URL-encode each substituted value so a value
// with & = / ? # space can't inject/split the URL. Mirrors the backend.
export function interpolateLandingVars(
  raw: string,
  vars: Record<string, string>,
  opts?: { encodeValues?: boolean },
): { text: string; unknown: string[] } {
  const unknown: string[] = [];
  const text = raw.replace(PLACEHOLDER_RE, (_m, name: string) => {
    if (Object.prototype.hasOwnProperty.call(vars, name)) {
      const v = vars[name];
      return opts?.encodeValues ? encodeURIComponent(v) : v;
    }
    unknown.push(name);
    return "";
  });
  return { text, unknown };
}

// Fragment-safe, encode-once query append (mirror of the backend util). Later
// pairs replace earlier same-named params (incl. those already in the URL's
// query). Existing params keep their original position + encoding when not
// overridden. Trailing #fragment preserved at the very end. Set encode=false
// for a human-readable preview that keeps {{placeholders}} legible.
export function appendParamsToUrl(
  url: string,
  pairs: Array<[string, string]>,
  encode = true,
): string {
  const hashIdx = url.indexOf("#");
  const fragment = hashIdx >= 0 ? url.slice(hashIdx) : "";
  const head = hashIdx >= 0 ? url.slice(0, hashIdx) : url;

  const qIdx = head.indexOf("?");
  const path = qIdx >= 0 ? head.slice(0, qIdx) : head;
  const existingQs = qIdx >= 0 ? head.slice(qIdx + 1) : "";

  const order: string[] = [];
  const tokenByName = new Map<string, string>();

  if (existingQs) {
    for (const tok of existingQs.split("&")) {
      if (!tok) {
        continue;
      }
      const eq = tok.indexOf("=");
      const name = eq >= 0 ? tok.slice(0, eq) : tok;
      if (!tokenByName.has(name)) {
        order.push(name);
      }
      tokenByName.set(name, tok);
    }
  }

  for (const [name, value] of pairs) {
    if (value === undefined || value === null || value === "") {
      continue;
    }
    if (!tokenByName.has(name)) {
      order.push(name);
    }
    tokenByName.set(
      name,
      encode
        ? `${encodeURIComponent(name)}=${encodeURIComponent(value)}`
        : `${name}=${value}`,
    );
  }

  const qs = order.map((n) => tokenByName.get(n)!).join("&");
  return path + (qs ? `?${qs}` : "") + fragment;
}

// Ordered [name, rawValue] param layers (fixed → custom → prefill), before
// interpolation. Used by both resolve + preview + validation so the ordering
// is defined once.
function collectParamLayers(config: LandingConfig): {
  fixed: Array<[string, string]>;
  custom: CustomParam[];
  prefill: string[];
} {
  const fixed: Array<[string, string]> = [];
  for (const key of LANDING_FIXED_PARAM_KEYS) {
    const raw = config[key];
    if (raw) {
      fixed.push([key, raw]);
    }
  }
  const custom = normalizeCustomParams(config.custom_params);
  const prefill = config.prefill_registration ? [...LANDING_PREFILL_KEYS] : [];
  return { fixed, custom, prefill };
}

// Resolved URL — EXACT mirror of the backend buildLandingPageLink. `vars` is a
// full contact bag (canonical + aliases). Returns null when disabled/no base.
export function resolveLandingUrl(
  config: LandingConfig | undefined,
  vars: Record<string, string>,
): string | null {
  if (!config?.enabled || !config.base_url) {
    return null;
  }
  const base = interpolateLandingVars(config.base_url, vars, {
    encodeValues: true,
  }).text;
  const { fixed, custom, prefill } = collectParamLayers(config);

  const pairs: Array<[string, string]> = [];
  for (const [key, raw] of fixed) {
    const v = interpolateLandingVars(raw, vars).text;
    if (v) {
      pairs.push([key, v]);
    }
  }
  for (const cp of custom) {
    if (!cp.name) {
      continue;
    }
    const v = interpolateLandingVars(cp.value ?? "", vars).text;
    if (v) {
      pairs.push([cp.name, v]);
    }
  }
  for (const key of prefill) {
    const source = key === "phone" ? "phone_number" : key;
    const v = (vars[source] ?? "").trim();
    if (v) {
      pairs.push([key, v]);
    }
  }
  return appendParamsToUrl(base, pairs);
}

// Human-readable preview when no contact is selected: keeps {{placeholders}}
// literal (unresolved, unencoded) so the operator sees param names + structure.
export function previewLandingUrl(config: LandingConfig | undefined): string {
  if (!config?.enabled || !config.base_url) {
    return "";
  }
  const { fixed, custom, prefill } = collectParamLayers(config);
  const pairs: Array<[string, string]> = [];
  for (const [key, raw] of fixed) {
    if (raw) {
      pairs.push([key, raw]);
    }
  }
  for (const cp of custom) {
    if (cp.name && cp.value) {
      pairs.push([cp.name, cp.value]);
    }
  }
  for (const key of prefill) {
    const source = key === "phone" ? "phone_number" : key;
    pairs.push([key, `{{${source}}}`]);
  }
  // Keep base_url literal (do not resolve), keep placeholders legible.
  return appendParamsToUrl(config.base_url, pairs, false);
}

// Trim rows and drop fully-blank ones. Partial rows are KEPT so validation can
// flag them; they are excluded from the sent URL by resolveLandingUrl.
export function normalizeCustomParams(
  rows: CustomParam[] | undefined,
): CustomParam[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows
    .map((r) => ({
      name: (r?.name ?? "").trim(),
      value: (r?.value ?? "").trim(),
      ...(r?.required ? { required: true } : {}),
    }))
    .filter((r) => r.name !== "" || r.value !== "");
}

// Any Handlebars-SHAPED token ({{...}}) whose inner content is NOT a clean,
// resolvable {{known_word}} — malformed names ({{zuora-account-id}}), unknown
// vars, stray braces. Mirrors the backend; these must never reach a send.
export function findUnresolvableTokens(
  config: LandingConfig | undefined,
): string[] {
  if (!config) {
    return [];
  }
  const bad = new Set<string>();
  const scan = (raw: string | undefined) => {
    if (!raw) {
      return;
    }
    const re = /\{\{([^{}]*)\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(raw)) !== null) {
      const inner = m[1].trim();
      if (!/^\w+$/.test(inner) || !KNOWN_LANDING_VARS.has(inner)) {
        bad.add(m[0]);
      }
    }
    // Unbalanced / extra braces (e.g. `{{x`, `{{{x}}}`): strip all balanced
    // {{...}} then flag any remaining brace character. Mirrors the backend.
    const stray = raw.replace(/\{\{[^{}]*\}\}/g, "").match(/\S*[{}]\S*/);
    if (stray) {
      bad.add(stray[0]);
    }
  };
  scan(config.base_url);
  for (const key of LANDING_FIXED_PARAM_KEYS) {
    scan(config[key]);
  }
  for (const cp of normalizeCustomParams(config.custom_params)) {
    scan(cp.value);
  }
  return [...bad];
}

// Every distinct placeholder referenced across base_url + fixed + custom values.
export function discoverPlaceholders(
  config: LandingConfig | undefined,
): string[] {
  if (!config) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  const scan = (raw: string | undefined) => {
    if (!raw) {
      return;
    }
    const re = new RegExp(PLACEHOLDER_RE.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(raw)) !== null) {
      if (!seen.has(m[1])) {
        seen.add(m[1]);
        out.push(m[1]);
      }
    }
  };
  scan(config.base_url);
  for (const key of LANDING_FIXED_PARAM_KEYS) {
    scan(config[key]);
  }
  for (const cp of normalizeCustomParams(config.custom_params)) {
    scan(cp.value);
  }
  return out;
}

// Variables referenced by custom params marked `required`. The pre-send modal
// uses this to escalate a missing REQUIRED value from a warning to a blocker.
export function requiredLandingVariables(
  config: LandingConfig | undefined,
): string[] {
  if (!config?.custom_params) {
    return [];
  }
  const out = new Set<string>();
  for (const cp of config.custom_params) {
    if (!cp?.required) {
      continue;
    }
    const re = new RegExp(PLACEHOLDER_RE.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(cp.value ?? "")) !== null) {
      out.add(m[1]);
    }
  }
  return [...out];
}

function isValidHttpUrl(raw: string): boolean {
  // Strip placeholders to a benign token so a URL that's valid apart from
  // {{vars}} still validates.
  const probe = raw.replace(PLACEHOLDER_RE, "x");
  try {
    const u = new URL(probe);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Validation for a landing config. Returns a flat list of human-readable
 * errors (empty ⇒ valid). Draft save may ignore these; scheduling/sending
 * must block on a non-empty list.
 */
export function validateLandingConfig(
  config: LandingConfig | undefined,
): string[] {
  if (!config?.enabled) {
    return [];
  }
  const errors: string[] = [];

  const baseUrl = (config.base_url ?? "").trim();
  if (!baseUrl) {
    errors.push("Landing page base URL is required.");
  } else if (!isValidHttpUrl(baseUrl)) {
    errors.push("Landing page base URL must be a valid http(s) URL.");
  }

  // Unresolvable/malformed tokens anywhere (unknown vars, bad names like
  // {{zuora-account-id}}, stray braces).
  for (const bad of findUnresolvableTokens(config)) {
    errors.push(`Unresolved or unsupported variable ${bad}.`);
  }

  // Custom-param row validation.
  const raw = Array.isArray(config.custom_params) ? config.custom_params : [];
  const seenNames = new Set<string>();
  const prefillSet = new Set<string>(
    config.prefill_registration ? LANDING_PREFILL_KEYS : [],
  );
  raw.forEach((r, i) => {
    const name = (r?.name ?? "").trim();
    const value = (r?.value ?? "").trim();
    if (name === "" && value === "") {
      return;
    } // fully blank → dropped, not an error
    if (name === "") {
      errors.push(`Custom parameter #${i + 1} has a value but no name.`);
      return;
    }
    if (!LANDING_PARAM_NAME_RE.test(name)) {
      errors.push(
        `Custom parameter name "${name}" has invalid characters (use letters, digits, _ . -).`,
      );
    }
    if (value === "") {
      errors.push(`Custom parameter "${name}" has a name but no value.`);
    }
    if (seenNames.has(name)) {
      errors.push(`Duplicate custom parameter name "${name}".`);
    }
    seenNames.add(name);
    if (prefillSet.has(name)) {
      errors.push(
        `Custom parameter "${name}" collides with a registration-prefill parameter and would be overridden — rename it or turn off prefill.`,
      );
    }
  });

  return errors;
}
